import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Spinner } from '@/components/ui/spinner';
import { Badge } from '@/components/ui/badge';
import {
  useGetAgentInboxSettingsQuery,
  useGetEmailDetailQuery,
  useGetInboxListQuery,
  useReplyToEmailMutation,
  useClassifyEmailMutation,
} from '@/store/emailInboxApi';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Reply, ExternalLink, Paperclip, Bot, Sparkles } from 'lucide-react';
import { HtmlSignatureEditor } from './HtmlSignatureEditor';

interface Props {
  emailId: number;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

const STATUS_COLOR: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  reply: 'bg-blue-100 text-blue-700',
  attached: 'bg-green-100 text-green-700',
  lead: 'bg-purple-100 text-purple-700',
  ignored: 'bg-gray-100 text-gray-500',
};

export function EmailDetailModal({ emailId, open, onOpenChange }: Props) {
  const { data: email, isLoading } = useGetEmailDetailQuery(emailId, { skip: !open });
  const [replyToEmail, { isLoading: isSending }] = useReplyToEmailMutation();
  const [classifyEmail, { isLoading: isClassifying }] = useClassifyEmailMutation();
  const { data: inboxes = [] } = useGetInboxListQuery();
  const { data: agents = [] } = useGetAgentInboxSettingsQuery();
  const [replyBody, setReplyBody] = useState('');
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [selectedInboxId, setSelectedInboxId] = useState<string>('');
  const [selectedAgentId, setSelectedAgentId] = useState<string>('none');
  const [signatureHtml, setSignatureHtml] = useState('');

  const sendInboxes = inboxes.filter((i) => i.is_active && i.direction !== 'receive_only');

  async function handleSendReply(e: React.FormEvent) {
    e.preventDefault();
    if (!replyBody.trim()) return;
    try {
      await replyToEmail({
        id: emailId,
        body: replyBody,
        from_inbox_id: selectedInboxId ? Number(selectedInboxId) : undefined,
        agent_id: selectedAgentId !== 'none' ? Number(selectedAgentId) : undefined,
        signature_html: signatureHtml,
      }).unwrap();
      toast.success('המייל נשלח בהצלחה');
      setReplyBody('');
      setShowReplyForm(false);
    } catch (err: unknown) {
      const msg = (err as { data?: { detail?: string } })?.data?.detail ?? 'שגיאה בשליחה';
      toast.error(msg);
    }
  }

  async function handleClassify() {
    try {
      await classifyEmail(emailId).unwrap();
      toast.success('סיווג AI הופעל ברקע');
    } catch {
      toast.error('שגיאה בהפעלת סיווג');
    }
  }

  function openReplyForm() {
    setShowReplyForm(true);
    if (email) {
      setSelectedInboxId(String(email.inbox));
    }
    setSelectedAgentId('none');
    setSignatureHtml('');
  }

  function onAgentChange(agentId: string) {
    setSelectedAgentId(agentId);
    if (agentId === 'none') {
      setSignatureHtml('');
      return;
    }
    const found = agents.find((a) => a.id === Number(agentId));
    if (found) {
      if (found.reply_inbox) {
        setSelectedInboxId(String(found.reply_inbox));
      }
      setSignatureHtml(found.signature_html || '');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-6" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">פרטי מייל</DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="flex justify-center py-12"><Spinner /></div>
        )}

        {email && (
          <div className="space-y-4">
            {/* Header section */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex flex-wrap gap-2 items-center">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLOR[email.status] ?? 'bg-gray-100 text-gray-600'}`}>
                  {email.status_display}
                </span>
                <Badge variant="outline">{email.inbox_label}</Badge>
                <span className="text-gray-400 text-xs">{email.inbox_email}</span>
              </div>

              <div>
                <span className="font-medium text-gray-500">נושא: </span>
                <span className="font-semibold">{email.subject}</span>
              </div>

              <div className="flex flex-wrap gap-4 text-gray-600">
                <span>
                  <span className="text-gray-400">שולח: </span>
                  {email.from_name && <strong>{email.from_name} </strong>}
                  <a href={`mailto:${email.from_email}`} className="text-blue-600 hover:underline">
                    {email.from_email}
                  </a>
                </span>
                <span>
                  <span className="text-gray-400">התקבל: </span>
                  {new Date(email.received_at).toLocaleString('he-IL')}
                </span>
              </div>

              {email.service_call_id && (
                <div>
                  <span className="text-gray-400">פניה מקושרת: </span>
                  <a
                    href={`/crm/servicecall?id=${email.service_call_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:underline inline-flex items-center gap-1"
                  >
                    #{email.service_call_id} — {email.service_call_subject}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
            </div>

            {/* AI result */}
            {email.ai_result && (
              <div className="border border-blue-100 rounded-lg p-3 bg-blue-50 text-xs space-y-1">
                <div className="flex items-center gap-2 font-medium text-blue-700">
                  <Bot className="h-4 w-4" />
                  תוצאת סיווג AI
                </div>
                <pre className="text-gray-700 whitespace-pre-wrap font-mono text-xs overflow-x-auto">
                  {JSON.stringify(email.ai_result, null, 2)}
                </pre>
              </div>
            )}

            {/* Attachments */}
            {email.attachments && email.attachments.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                  <Paperclip className="h-3 w-3" />
                  קבצים מצורפים ({email.attachments.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {email.attachments.map((url) => (
                    <a
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-blue-600 hover:underline"
                    >
                      {url.split('/').pop()}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Body */}
            <div className="border rounded-lg overflow-hidden">
              <div className="px-4 py-2 bg-gray-50 border-b text-xs font-medium text-gray-500">
                תוכן המייל
              </div>
              {email.body_html ? (
                <iframe
                  srcDoc={email.body_html}
                  className="w-full min-h-[300px] border-0"
                  sandbox="allow-popups allow-popups-to-escape-sandbox"
                  title="תוכן מייל"
                />
              ) : (
                <pre className="p-4 text-sm text-gray-700 whitespace-pre-wrap font-sans">
                  {email.body_text || '(ללא תוכן)'}
                </pre>
              )}
            </div>

            {/* Reply section */}
            <div className="border-t pt-4">
              {!showReplyForm ? (
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    onClick={openReplyForm}
                    className="gap-2"
                  >
                    <Reply className="h-4 w-4" />
                    השב למייל זה
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleClassify}
                    disabled={isClassifying}
                    className="gap-2 text-purple-700 border-purple-200 hover:bg-purple-50"
                  >
                    <Sparkles className="h-4 w-4" />
                    {isClassifying ? 'מסווג...' : 'סווג עם AI'}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSendReply} className="space-y-3">
                  <p className="text-sm font-medium">
                    השב לְ: <span className="text-blue-600">{email.from_email}</span>
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>תיבת שליחה</Label>
                      <Select value={selectedInboxId} onValueChange={setSelectedInboxId}>
                        <SelectTrigger>
                          <SelectValue placeholder="בחר תיבת שליחה" />
                        </SelectTrigger>
                        <SelectContent>
                          {sendInboxes.map((inbox) => (
                            <SelectItem key={inbox.id} value={String(inbox.id)}>
                              {inbox.email}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <Label>בשם נציג</Label>
                      <Select value={selectedAgentId} onValueChange={onAgentChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="ללא נציג" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">ללא נציג</SelectItem>
                          {agents.map((agent) => (
                            <SelectItem key={agent.id} value={String(agent.id)}>
                              {agent.publish_name || agent.name || `נציג #${agent.id}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <HtmlSignatureEditor value={signatureHtml} onChange={setSignatureHtml} label="חתימה (HTML)" />

                  <Textarea
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    placeholder="תוכן ההשבה..."
                    rows={6}
                    required
                    className="resize-y"
                  />
                  <div className="flex gap-2">
                    <Button type="submit" disabled={isSending || !replyBody.trim()}>
                      {isSending ? 'שולח...' : 'שלח'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowReplyForm(false);
                        setReplyBody('');
                        setSelectedAgentId('none');
                        setSignatureHtml('');
                      }}
                    >
                      ביטול
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
